<template>
  <Transition name="slide-up">
    <div v-if="shouldShowPrompt" class="install-prompt-overlay">
      <div class="install-prompt">
        <button class="close-btn" @click="dismissPrompt" aria-label="סגור">
          <span class="material-icons">close</span>
        </button>

        <div class="prompt-content">
          <div class="app-icon">
            <img src="/img/icons/logo.svg" alt="הדרך אל הלב" />
          </div>

          <div class="prompt-text">
            <h3>התקן את האפליקציה</h3>
            <p v-if="isIOS">
              הוסף את האפליקציה למסך הבית לגישה מהירה וחוויה משופרת
            </p>
            <p v-else>
              התקן את האפליקציה לגישה מהירה וחוויה משופרת
            </p>
          </div>
        </div>

        <!-- iOS Safari Instructions -->
        <div v-if="isIOSSafari" class="ios-instructions">
          <p class="browser-label">
            <span class="material-icons">phone_iphone</span>
            הוראות ל-Safari
          </p>
          <p class="instruction-step">
            <span class="step-number">1</span>
            לחץ על כפתור השיתוף בסרגל התחתון
          </p>
          <div class="step-illustration">
            <div class="safari-bar-mockup">
              <span class="material-icons bar-icon">arrow_back_ios</span>
              <span class="material-icons bar-icon">arrow_forward_ios</span>
              <span class="material-icons bar-icon highlight-icon">
                <svg class="share-svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="5" y="9" width="14" height="12" rx="2" />
                  <line x1="12" y1="2" x2="12" y2="14" />
                  <polyline points="8,6 12,2 16,6" />
                </svg>
              </span>
              <span class="material-icons bar-icon">book</span>
              <span class="material-icons bar-icon">tab</span>
            </div>
          </div>
          <p class="instruction-step">
            <span class="step-number">2</span>
            גלול למטה ובחר "הוסף למסך הבית"
          </p>
          <div class="step-illustration">
            <div class="add-home-mockup">
              <span class="material-icons mockup-icon">add_box</span>
              <span class="mockup-text">הוסף למסך הבית</span>
            </div>
          </div>
          <p class="instruction-step">
            <span class="step-number">3</span>
            לחץ "הוסף" בפינה העליונה
          </p>
        </div>

        <!-- iOS Chrome Instructions -->
        <div v-else-if="isIOSChrome" class="ios-instructions">
          <p class="browser-label">
            <span class="material-icons">phone_iphone</span>
            הוראות ל-Chrome
          </p>
          <p class="instruction-step">
            <span class="step-number">1</span>
            לחץ על שלוש הנקודות (⋯) בסרגל הכלים
          </p>
          <div class="step-illustration">
            <div class="chrome-bar-mockup">
              <span class="material-icons bar-icon">arrow_back</span>
              <div class="chrome-url-bar">heart-app.co.il</div>
              <span class="material-icons bar-icon highlight-icon">more_horiz</span>
            </div>
          </div>
          <p class="instruction-step">
            <span class="step-number">2</span>
            בחר "הוסף למסך הבית"
          </p>
          <div class="step-illustration">
            <div class="add-home-mockup">
              <span class="material-icons mockup-icon">add_box</span>
              <span class="mockup-text">הוסף למסך הבית</span>
            </div>
          </div>
        </div>

        <!-- Other iOS Browsers (Firefox, Edge, etc.) -->
        <div v-else-if="isIOS" class="ios-instructions">
          <p class="instruction-step">
            <span class="step-number">1</span>
            לחץ על כפתור השיתוף
            <span class="material-icons share-icon">ios_share</span>
          </p>
          <p class="instruction-step">
            <span class="step-number">2</span>
            בחר "הוסף למסך הבית"
            <span class="material-icons">add_box</span>
          </p>
          <p class="instruction-note">
            <span class="material-icons">info</span>
            אם האפשרות לא זמינה, נסה לפתוח את האתר דרך Safari
          </p>
        </div>

        <!-- Android/Chrome Install Button -->
        <div v-else class="prompt-actions">
          <button class="install-btn" @click="installApp">
            <span class="material-icons">download</span>
            התקן עכשיו
          </button>
          <button class="later-btn" @click="dismissPrompt">
            אולי אחר כך
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { states } from '@/main_store';


const route = useRoute();
const dismissed = ref(false);

// Session-based dismiss key (resets when browser closes)
const SESSION_DISMISS_KEY = 'pwa-install-dismissed-session';

// Check if running in standalone mode (already installed)
const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
};

// Check if device is iOS
const isIOS = computed(() => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
});

// Check if running in Safari on iOS (not Chrome/Firefox)
const isIOSSafari = computed(() => {
  if (!isIOS.value) return false;
  return /safari/i.test(navigator.userAgent) &&
         !/crios|fxios|opera/i.test(navigator.userAgent);
});

// Check if running in Chrome on iOS
const isIOSChrome = computed(() => {
  if (!isIOS.value) return false;
  return /crios/i.test(navigator.userAgent);
});

// Check if device is mobile
const isMobile = computed(() => {
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    window.navigator.userAgent.toLowerCase()
  );
});

// Check if on Home page
const isHomePage = computed(() => route.name === 'Home');

// Check if auth is ready
const authIsReady = computed(() => states.value.authIsReady);

// Check if we have the install prompt (Android/Chrome)
const hasInstallPrompt = computed(() => !!states.value.deferredInstallPrompt);

// Main condition: should we show the prompt?
const shouldShowPrompt = computed(() => {
  // Must be on Home page
  if (!isHomePage.value) return false;

  // Must be on mobile
  if (!isMobile.value) return false;

  // Must not be already installed
  if (isStandalone()) return false;

  // Must not be dismissed this session
  if (dismissed.value) return false;

  // Auth must be ready
  if (!authIsReady.value) return false;

  // For iOS: always show instructions (iOS doesn't support beforeinstallprompt)
  if (isIOS.value) return true;

  // For Android/Chrome: show if we have the install prompt
  if (hasInstallPrompt.value) return true;

  return false;
});

// Install the app (Android/Chrome)
const installApp = async () => {
  const prompt = states.value.deferredInstallPrompt;
  if (!prompt) return;

  prompt.prompt();
  const { outcome } = await prompt.userChoice;

  if (outcome === 'accepted') {
    console.log('PWA installed');
  }

  // Clear the prompt after use
  states.value.deferredInstallPrompt = null;
};

// Dismiss the prompt for this session
const dismissPrompt = () => {
  dismissed.value = true;
  sessionStorage.setItem(SESSION_DISMISS_KEY, 'true');
};

// Handle app installed event
const handleAppInstalled = () => {
  states.value.deferredInstallPrompt = null;
};

onMounted(() => {
  // Check if dismissed this session
  if (sessionStorage.getItem(SESSION_DISMISS_KEY) === 'true') {
    dismissed.value = true;
  }

  // Listen for app installed event
  window.addEventListener('appinstalled', handleAppInstalled);
});

onUnmounted(() => {
  window.removeEventListener('appinstalled', handleAppInstalled);
});
</script>

<style scoped>
.install-prompt-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  padding: 0 10px 10px 10px;
}

.install-prompt {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  padding: 20px;
  direction: rtl;
  position: relative;
  max-width: 500px;
  margin: 0 auto;
}

.close-btn {
  position: absolute;
  top: 10px;
  left: 10px;
  background: transparent;
  border: none;
  padding: 5px;
  cursor: pointer;
  color: #666;
  box-shadow: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 50%;
}

.close-btn .material-icons {
  font-size: 24px;
  color: #666;
}

.prompt-content {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.app-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-icon img {
  width: 50px;
  height: 50px;
  object-fit: contain;
}

.prompt-text {
  flex: 1;
}

.prompt-text h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.prompt-text p {
  margin: 0;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}

.ios-instructions {
  background: #f5f5f5;
  border-radius: 10px;
  padding: 15px;
  margin-top: 10px;
}

.instruction-step {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0;
  font-size: 14px;
  color: #333;
}

.instruction-step:first-child {
  margin-top: 0;
}

.instruction-step:last-child {
  margin-bottom: 0;
}

.step-number {
  background: var(--dark-blue, #026acc);
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.instruction-step .material-icons {
  font-size: 20px;
  color: var(--dark-blue, #026acc);
  margin-right: auto;
}

.share-icon {
  transform: translateY(-2px);
}

/* Browser label */
.browser-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #888;
  margin-bottom: 10px;
  font-weight: 600;
}

.browser-label .material-icons {
  font-size: 16px;
  color: #888;
}

/* Step illustrations */
.step-illustration {
  margin: 8px 0 14px 0;
  display: flex;
  justify-content: center;
}

/* Safari bottom bar mockup */
.safari-bar-mockup {
  display: flex;
  align-items: center;
  justify-content: space-around;
  direction: ltr;
  background: #f2f2f7;
  border-radius: 12px;
  padding: 8px 12px;
  width: 100%;
  max-width: 280px;
  border: 1px solid #e0e0e0;
}

.bar-icon {
  font-size: 20px;
  color: #007aff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.highlight-icon {
  background: rgba(2, 106, 204, 0.12);
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--dark-blue, #026acc);
  color: var(--dark-blue, #026acc);
}

.share-svg {
  display: block;
}

/* Chrome bar mockup */
.chrome-bar-mockup {
  display: flex;
  align-items: center;
  direction: ltr;
  gap: 10px;
  background: #f2f2f7;
  border-radius: 12px;
  padding: 8px 12px;
  width: 100%;
  max-width: 280px;
  border: 1px solid #e0e0e0;
}

/* Chrome URL bar mockup */
.chrome-url-bar {
  flex: 1;
  background: #e8e8ed;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  color: #666;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* "Add to Home Screen" row mockup */
.add-home-mockup {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border-radius: 10px;
  padding: 10px 14px;
  width: 100%;
  max-width: 280px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.mockup-icon {
  font-size: 22px;
  color: #007aff;
}

.mockup-text {
  font-size: 14px;
  color: #1a1a1a;
  font-weight: 500;
}

/* Hint note for other browsers */
.instruction-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px;
  background: #fff3e0;
  border-radius: 8px;
  font-size: 13px;
  color: #e65100;
  line-height: 1.4;
}

.instruction-note .material-icons {
  font-size: 18px;
  color: #e65100;
  flex-shrink: 0;
}

.prompt-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.install-btn {
  flex: 1;
  background: var(--dark-blue, #026acc);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
}

.install-btn:hover {
  background: var(--light-blue, #42A5F5);
}

.install-btn .material-icons {
  font-size: 20px;
  color: white;
}

.later-btn {
  background: transparent;
  color: #666;
  border: 1px solid #ddd;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: none;
}

.later-btn:hover {
  background: #f5f5f5;
  border-color: #ccc;
}

/* Slide up animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 400px) {
  .install-prompt {
    padding: 15px;
  }

  .app-icon {
    width: 50px;
    height: 50px;
  }

  .app-icon img {
    width: 40px;
    height: 40px;
  }

  .prompt-text h3 {
    font-size: 16px;
  }

  .prompt-text p {
    font-size: 13px;
  }

  .prompt-actions {
    flex-direction: column;
  }

  .later-btn {
    order: 1;
  }
}
</style>
